import {useCallback, useEffect, useRef, useState} from 'react';

import {validateSamlCertificate} from 'src/openapi/admin/apis/SsoApi';
import {useAppDispatch} from 'src/store/hooks';

import type {Error as ApiError} from 'src/openapi/admin/models/Error';

type UseValidateCertificateReturn = {
    validate: (certificate: string) => Promise<boolean>,
    loading: boolean,
    error: ApiError | null,
    clearError: () => void,
};

export function useValidateCertificate(): UseValidateCertificateReturn {
    const dispatch = useAppDispatch();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<ApiError | null>(null);
    const abortControllerRef = useRef<AbortController | null>(null);

    useEffect(() => {
        return () => {
            abortControllerRef.current?.abort();
        };
    }, []);

    const validate = useCallback(async (certificate: string): Promise<boolean> => {
        abortControllerRef.current?.abort();
        const controller = new AbortController();
        abortControllerRef.current = controller;

        setLoading(true);
        setError(null);

        try {
            const response = await dispatch(
                validateSamlCertificate(
                    {sertificateValidatorRequest: {certificate}},
                    {signal: controller.signal},
                ),
            );

            if (controller.signal.aborted) {
                return false;
            }

            if (response.payload?.success) {
                return true;
            }

            setError(response.payload?.data
                ?? {detailMessages: ['Certificate validation failed. Please check the certificate and try again.']});
            return false;
        } catch (err) {
            if ((err as {name?: string})?.name === 'AbortError') {
                return false;
            }
            setError({detailMessages: ['Failed to reach the server. Please check your connection and try again.']});
            return false;
        } finally {
            if (abortControllerRef.current === controller) {
                abortControllerRef.current = null;
            }
            setLoading(false);
        }
    }, [dispatch]);

    const clearError = useCallback(() => {
        setError(null);
    }, []);

    return {validate, loading, error, clearError};
}
